import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaClipboardList,
  FaDownload,
  FaSearch,
  FaSpinner,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ModalPesquisa from "../../components/modal/modalPesquisa";
import api from "../../services/axios";
import { formatarDiaHora } from "../../services/conversaoTimer";
import exportToExcel from "../../services/exportExcel";
import "./admPatrocinadoresList.css";
import "./admReuniaoResultado.css";

const AVALIACOES = [
  "",
  "",
  "Não foi boa",
  "Aproximou relacionamento",
  "Gerou proposta",
  "Vamos fechar negócio",
];

function idUsuarioParticipante(participante) {
  return Number(participante?.id_usuario ?? participante?.idUsuario ?? 0);
}

function nomeParticipante(participante) {
  return participante?.usuario?.nome?.trim() || "—";
}

function formatarDataReuniao(registro) {
  const dt =
    registro?.agenda?.dt_hora_inicio ||
    registro?.agenda?.dtHoraInicio ||
    registro?.presenca;
  if (!dt) return "—";
  return formatarDiaHora(String(dt)) || "—";
}

function textoAvaliacao(participante) {
  if (Number(participante?.status) === 1) {
    return AVALIACOES[participante?.rate] || "Avaliado";
  }
  return "Não avaliado";
}

/** Linha em que o participante é o patrocinador, com presença e pesquisa pendente. */
function ehPendentePatrocinador(registro) {
  if (!registro?.presenca || !registro?.agenda?.participantes?.length) {
    return false;
  }
  const patrocinador = registro.agenda.participantes[1];
  if (!patrocinador || Number(patrocinador.status) !== 0) {
    return false;
  }
  return idUsuarioParticipante(registro) === idUsuarioParticipante(patrocinador);
}

const AdmReuniaoResultado = () => {
  const navigate = useNavigate();
  const [resultados, setResultados] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isExportando, setIsExportando] = useState(false);
  const [error, setError] = useState(null);
  const [showModalPesquisa, setShowModalPesquisa] = useState(false);
  const [pesquisa, setPesquisa] = useState(null);

  const carregarResultados = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await api.post("/pesquisa-reunioes-adm", {});
      if (resp.status === 200) {
        const pendentes = (resp.data?.pesquisas || [])
          .filter(ehPendentePatrocinador)
          .sort((a, b) => {
            const nomeA = nomeParticipante(a.agenda?.participantes?.[1]);
            const nomeB = nomeParticipante(b.agenda?.participantes?.[1]);
            return nomeA.localeCompare(nomeB, "pt-BR");
          });
        setResultados(pendentes);
      }
    } catch (err) {
      console.error("Erro ao carregar resultados de reuniões:", err);
      setError("Erro ao carregar reuniões não avaliadas.");
      setResultados([]);
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao carregar reuniões não avaliadas."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarResultados();
  }, [carregarResultados]);

  const listaFiltrada = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    if (!termo) return resultados;
    return resultados.filter((r) => {
      const patro = nomeParticipante(r.agenda?.participantes?.[1]).toLowerCase();
      const convidado = nomeParticipante(
        r.agenda?.participantes?.[0]
      ).toLowerCase();
      const empresa = (
        r.agenda?.participantes?.[0]?.usuario?.empresa || ""
      ).toLowerCase();
      const cargo = (
        r.agenda?.participantes?.[0]?.usuario?.cargo || ""
      ).toLowerCase();
      return (
        patro.includes(termo) ||
        convidado.includes(termo) ||
        empresa.includes(termo) ||
        cargo.includes(termo)
      );
    });
  }, [resultados, filtro]);

  const indicadores = useMemo(() => {
    const patrocinadores = new Set(
      listaFiltrada.map((r) =>
        nomeParticipante(r.agenda?.participantes?.[1])
      )
    );
    return {
      total: listaFiltrada.length,
      patrocinadores: patrocinadores.size,
    };
  }, [listaFiltrada]);

  const abrirPesquisa = (reuniao) => {
    const participantePatro = reuniao?.agenda?.participantes?.[1];
    if (!participantePatro?.id) {
      toast.error("Não foi possível abrir a pesquisa desta reunião.");
      return;
    }
    setPesquisa({
      id: participantePatro.id,
      agenda: reuniao.agenda,
    });
    setShowModalPesquisa(true);
  };

  const fecharModalPesquisa = () => {
    setShowModalPesquisa(false);
    setPesquisa(null);
  };

  const handleDownload = async () => {
    if (!listaFiltrada.length) {
      toast.warning("Não há reuniões para exportar.");
      return;
    }

    setIsExportando(true);
    try {
      const linhas = listaFiltrada.map((r) => {
        const convidado = r.agenda?.participantes?.[0];
        const patrocinador = r.agenda?.participantes?.[1];
        return {
          Patrocinador: nomeParticipante(patrocinador),
          Convidado: `${nomeParticipante(convidado)}${
            convidado?.usuario?.cargo
              ? ` / ${convidado.usuario.cargo}`
              : ""
          }`,
          Empresa: convidado?.usuario?.empresa || "",
          "Data reunião": formatarDataReuniao(r),
          "Resultado patrocinador": textoAvaliacao(patrocinador),
        };
      });
      await exportToExcel(linhas, "Reuniões não avaliadas pelos patrocinadores");
      toast.success("Planilha exportada com sucesso.");
    } catch (err) {
      console.error("Erro ao exportar Excel:", err);
      toast.error("Erro ao exportar a planilha.");
    } finally {
      setIsExportando(false);
    }
  };

  return (
    <div className="apl-page apl-page--padded arr-page">
      <ModalPesquisa
        titulo="Pesquisa da Reunião"
        show={showModalPesquisa}
        toggleModal={fecharModalPesquisa}
        pesquisa={pesquisa}
        tipoUsuario="patrocinador"
        close={true}
        onRespondido={carregarResultados}
      />

      <header className="apl-header" aria-label="Cabeçalho resultados de reunião">
        <div className="apl-header-text">
          <h1>Resultados de reunião</h1>
          <p>
            Acompanhe as reuniões com presença em que o patrocinador ainda não
            respondeu a pesquisa de avaliação.
          </p>
        </div>

        <div className="apl-toolbar apl-toolbar--inline">
          <div className="apl-search arr-search">
            <span className="apl-search-icon" aria-hidden>
              <FaSearch size={14} />
            </span>
            <input
              type="search"
              className="apl-search-input"
              placeholder="Buscar patrocinador, convidado, empresa…"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              disabled={isLoading}
              aria-label="Buscar reuniões"
            />
          </div>
          <div className="apl-toolbar-actions">
            <button
              type="button"
              className="apl-btn apl-btn--secondary"
              onClick={handleDownload}
              disabled={isLoading || isExportando || listaFiltrada.length === 0}
            >
              {isExportando ? (
                <FaSpinner className="fa-spin" size={14} aria-hidden />
              ) : (
                <FaDownload size={14} aria-hidden />
              )}
              {isExportando ? "Exportando…" : "Exportar Excel"}
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              disabled={isExportando}
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </div>
      </header>

      <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
        {error ? (
          <div className="apl-alert" role="alert">
            {error}
          </div>
        ) : null}

        {!isLoading ? (
          <section className="apl-metrics" aria-label="Indicadores">
            <div className="apl-metric apl-metric--total">
              <span className="apl-metric-value">{indicadores.total}</span>
              <span className="apl-metric-label">
                {indicadores.total === 1
                  ? "Reunião não avaliada"
                  : "Reuniões não avaliadas"}
              </span>
            </div>
            <div className="apl-metric">
              <span className="apl-metric-value">
                {indicadores.patrocinadores}
              </span>
              <span className="apl-metric-label">
                {indicadores.patrocinadores === 1
                  ? "Patrocinador pendente"
                  : "Patrocinadores pendentes"}
              </span>
            </div>
          </section>
        ) : null}

        {isLoading ? (
          <div className="apl-card">
            <div className="apl-loading">
              <FaSpinner className="fa-spin" size={28} aria-hidden />
              <span>Carregando resultados…</span>
            </div>
          </div>
        ) : (
          <section className="apl-card" aria-label="Tabela de resultados">
            <div className="apl-table-wrap">
              {listaFiltrada.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">
                    {resultados.length === 0
                      ? "Nenhuma reunião pendente"
                      : "Nenhum resultado"}
                  </p>
                  <p style={{ margin: 0 }}>
                    {resultados.length === 0
                      ? "Não há reuniões com presença aguardando avaliação do patrocinador."
                      : "Ajuste a busca para ver outras reuniões."}
                  </p>
                </div>
              ) : (
                <table className="apl-table">
                  <thead>
                    <tr>
                      <th scope="col">Patrocinador</th>
                      <th scope="col">Convidado</th>
                      <th scope="col">Empresa</th>
                      <th scope="col">Data reunião</th>
                      <th scope="col" style={{ textAlign: "right" }}>
                        Resultado
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {listaFiltrada.map((r) => {
                      const convidado = r.agenda?.participantes?.[0];
                      const patrocinador = r.agenda?.participantes?.[1];
                      const key = r.id || `${r.agenda?.id}-${idUsuarioParticipante(r)}`;
                      return (
                        <tr key={key}>
                          <td className="apl-cell-name">
                            {nomeParticipante(patrocinador)}
                          </td>
                          <td>
                            <div className="arr-convidado">
                              <span className="arr-convidado-nome">
                                {nomeParticipante(convidado)}
                              </span>
                              {convidado?.usuario?.cargo ? (
                                <span className="arr-convidado-cargo">
                                  {convidado.usuario.cargo}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>{convidado?.usuario?.empresa || "—"}</td>
                          <td className="arr-cell-date">
                            {formatarDataReuniao(r)}
                          </td>
                          <td>
                            <div className="apl-actions arr-actions">
                              {Number(patrocinador?.status) === 1 ? (
                                <span className="arr-status arr-status--ok">
                                  {textoAvaliacao(patrocinador)}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="apl-action-btn"
                                  onClick={() => abrirPesquisa(r)}
                                >
                                  <FaClipboardList size={12} aria-hidden />
                                  Responder pesquisa
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AdmReuniaoResultado;
